import {Component, OnInit} from '@angular/core';
import {FormControl} from "@angular/forms";
import {ActivatedRoute} from "@angular/router";
import * as fromStore from "../../../../../store";
import {debounceTime, filter, first, takeUntil, tap} from "rxjs/operators";
import {Store} from "@ngrx/store";
import {Subject} from "rxjs";
import {IEcommerceOrdersEntityState} from "../../../../../store/reducers/ecommerce-orders.reducer";
import {IOrdersParams, OrderStatusEnum, PaymentStatusEnum} from "../orders.interface";
import {NotifierComponent} from "../../../../../shared/components/notifier/notifier.component";
import {Notification} from "../../../../../shared/components/notifier/notification";
import {NotificationType} from "../../../../../shared/components/notifier/notification-type";
import {OrdersService} from "../orders.service";
import {RemoveItemDialogComponent} from "../../products/remove-item-dialog/remove-item-dialog.component";
import {Config} from "../../../../../config/config";
import {EcommerceAnalyticsLabel} from "../../ecommerce-product.enum";
import {EcommerceService} from "../../ecommerce.service";
import {DbModalService} from "../../../../../modules/modal/modal";
import {TextUtils} from "../../../../../shared/utils/text-utils";

@Component({
    selector: 'db-orders-list',
    templateUrl: './orders-list.component.html',
    styleUrls: ['./orders-list.component.scss'],
    standalone: false
})
export class OrdersListComponent implements OnInit {
  tabs = [
    {
      id: 'any',
      title: TextUtils.getText('text_all')
    },
    {
      id: OrderStatusEnum.PENDING,
      title: TextUtils.getText('text_pending')
    },
    {
      id: OrderStatusEnum.PROCESSING,
      title: TextUtils.getText('text_processing')
    },
    {
      id: OrderStatusEnum.ON_HOLD,
      title: TextUtils.getText('text_onHold')
    },
    {
      id: OrderStatusEnum.COMPLETED,
      title: TextUtils.getText('text_completed')
    },
    {
      id: OrderStatusEnum.CANCELLED,
      title: TextUtils.getText('text_cancelled')
    },
    {
      id: OrderStatusEnum.REFUNDED,
      title: TextUtils.getText('text_refunded')
    },
    {
      id: OrderStatusEnum.FAILED,
      title: TextUtils.getText('text_failed')
    },
    {
      id: OrderStatusEnum.DRAFT,
      title: TextUtils.getText('text_draft')
    },
  ];
  activeTab = 'any';
  isMobile = false;
  searchTerm: FormControl = new FormControl();
  website = this._activatedRoute.snapshot.parent.parent.parent.data['website'];
  loading = true;
  orders: any[] = [];
  ordersCount = 1000;
  ordersPerPage = 10;
  currentPage = 1;
  private _subscription$: Subject<void> = new Subject<void>();
  isFakeMode = this.website.id === Config.demoDomainId;
  showSearchLoading = false;
  searched = false;
  isScrollingHorizontal = false;
  paymentMode: string;
  private _analyticsActionName = 'Ecommerce - Orders';

  constructor(private _activatedRoute: ActivatedRoute,
              private _store: Store<fromStore.DbStoreState>,
              private _ordersService: OrdersService,
              private _ecommerceService: EcommerceService,
              private _modalService: DbModalService) {
  }

  ngOnInit() {
    this._dispatchOrders();
    this._getOrders();
    this._paymentMethods();
    this._listenSearch();
    this._listenFailure();
  }


  onScroll(event: any) {
    this.isScrollingHorizontal = event.target.scrollLeft > 0;
  }

  private _dispatchOrders(showLoading = true) {
    const params: IOrdersParams = {
      websiteId: this.website.id,
      status: this.activeTab,
      page: this.currentPage,
      per_page: this.ordersPerPage
    };
    this.loading = showLoading;
    if (this.searchTerm.value) {
      params.search = this.searchTerm.value;
    }

    this._store.dispatch(new fromStore.LoadEcommerceOrders(params));
  }

  private _listenFailure() {
    // this._store.select(fromStore.getOrdersLoading(this.website.id))
    //   .pipe(takeUntil(this._subscription$))
    //   .subscribe((loading: boolean) => this.loading = loading);
  }

  private _getOrders() {
    this._store.select(fromStore.getOrders(this.website.id))
      .pipe(
        filter((prodState: IEcommerceOrdersEntityState) => prodState && prodState.loaded),
        takeUntil(this._subscription$)
      ).subscribe((response) => {
      this.searched = !!this.searchTerm.value;
      this.orders = response.orders;
      this.orders && this.orders.forEach(order => {
        order.hasTestEnvironment = order.meta_data?.some(item => item.key === 'twwp_environment' && item.value === 'test');
      });
      this.ordersCount = response.ordersCount;
      this.showSearchLoading = false;
      this.loading = false;
    });
  }

  private _paymentMethods(): void {
    this._store.select(fromStore.getEcommercePaymentMode)
      .pipe(
        first((loaded) => !!loaded ),
        filter((paymentMode) => !!paymentMode),
        takeUntil(this._subscription$)
      )
      .subscribe( paymentMode => {
        if (paymentMode) {
          this.paymentMode = paymentMode;
        }
      });
  }

  changeStatus(value: { orderId: number, status: string }) {
    const {orderId, status} = value;
    this._ordersService.updateOrder(this.website.id, orderId, {status})
      .subscribe((response) => {
        if (!response) {
          return;
        }
        if (response && response.success) {
          this.orders = this.orders.map(order => {
            if (order.id === response.data.dataKey.id) {
              order.status = response.data.dataKey.status;
              order.payment_status = PaymentStatusEnum[order.status];
            }
            return order;
          });
          if (this.activeTab !== 'any') {
            this.orders = this.orders.filter((prod) => {
              if (prod.id === value.orderId) {
                return this.activeTab === value.status;
              }
              return true;
            });
          }
          NotifierComponent.notify(new Notification(TextUtils.getText('text_statusUpdated'), NotificationType.SUCCESS));
          this._ecommerceService.sendAnalyticsEvent(this._analyticsActionName, EcommerceAnalyticsLabel.CHANGE_STATUS);
          return;
        }
        NotifierComponent.notify(new Notification(TextUtils.getText('text_somethingWentWrong'), NotificationType.DANGER));
        this._ecommerceService.sendAnalyticsEvent(this._analyticsActionName, EcommerceAnalyticsLabel.CHANGE_STATUS, true);
      });
  }

  private _listenSearch() {
    this.searchTerm.valueChanges.pipe(
      tap(() => this.showSearchLoading = true),
      debounceTime(500),
      takeUntil(this._subscription$)
    ).subscribe((searchValue) => {
      this.currentPage = 1;
      this._dispatchOrders(false);
    });
  }

  setActiveTab(id: string) {
    this.activeTab = id;
    this.currentPage = 1;
    this._dispatchOrders();
  }

  changePage(page: number) {
    this.currentPage = page;
    this._dispatchOrders();
  }

  onRemoveItem(id: number) {
    const modal = this._modalService
      .open(RemoveItemDialogComponent);
    modal.closed.subscribe(value => {
      if (value) {
        this._removeItem(id);
      }
    });
  }

  private _removeItem(id: number) {
    this.loading = true;
    this._ordersService.deleteOrder(this.website.id, id)
      .subscribe((value) => {
        if (!value) {
          return;
        }
        if (value.success) {
          NotifierComponent.notify(new Notification(TextUtils.getText('text_orderDeleted'), NotificationType.SUCCESS));
          this._ecommerceService.sendAnalyticsEvent(this._analyticsActionName, EcommerceAnalyticsLabel.DELETE);
          this._dispatchOrders();
          return;
        }
        NotifierComponent.notify(new Notification(TextUtils.getText('text_somethingWentWrong'), NotificationType.DANGER));
        this._ecommerceService.sendAnalyticsEvent(this._analyticsActionName, EcommerceAnalyticsLabel.DELETE, true);
      });
  }


}
